<template>
  <ErrorBoundary>
    <div id="app" class="min-h-screen">
      <!-- Conditional layout based on route -->
      <AppLayout v-if="!isPublicRoute">
        <RouterView v-slot="{ Component }">
          <Transition
            name="page"
            mode="out-in"
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 translate-y-4"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-4"
          >
            <component :is="Component" :key="$route.path" />
          </Transition>
        </RouterView>
      </AppLayout>

      <!-- Public pages without layout -->
      <RouterView v-else v-slot="{ Component }">
        <Transition
          name="page"
          mode="out-in"
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <component :is="Component" :key="$route.path" />
        </Transition>
      </RouterView>

      <!-- Global Conflict Resolution Components -->
      <ConflictNotification
        :is-visible="showConflictNotification"
        :message="notificationMessage"
        @close="dismissConflictNotification"
        @resolve="currentConflict && showConflictResolutionDialog(currentConflict)"
        @dismiss="dismissConflictNotification"
      />

      <ConflictResolutionDialog
        :is-open="showConflictDialog"
        :conflict-data="currentConflict"
        @close="dismissConflictDialog"
        @resolved="resolveConflict"
      />
    </div>
  </ErrorBoundary>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import AppLayout from './components/AppLayout.vue'

import ConflictNotification from './components/ConflictNotification.vue'
import ConflictResolutionDialog from './components/ConflictResolutionDialog.vue'
import ErrorBoundary from './components/ErrorBoundary.vue'

import { useConflictResolution } from './composables/useConflictResolution'
import type { RouteMeta } from './router'

const route = useRoute()

const {
  currentConflict,
  showConflictDialog,
  showConflictNotification,
  notificationMessage,
  showConflictResolutionDialog,
  resolveConflict,
  dismissConflictNotification,
  dismissConflictDialog
} = useConflictResolution()

// Check if current route is public (login, register)
const isPublicRoute = computed(() => {
  const meta = route.meta as RouteMeta
  return meta?.public === true
})
</script>